import { IcUser } from '@assets/icons/24x24';
import {
  Button,
  Center,
  Field as ChakraField,
  Fieldset,
  Flex,
  Icon,
  Input,
  InputGroup,
  Text,
  VStack,
} from '@chakra-ui/react';
import ErrorsContainer from '@components/common/form/ErrorsContainer';
import { Form } from '@components/ui/Form';
import { PasswordInput } from '@components/ui/password-input';
import { useAuth } from '@hooks/useAuth';
import useValidation from '@hooks/useValidation';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { updateIsChangingShift, userLogout } from '@redux/authent/authent.reducer';
import { LoginRequest, useLoginMutation, useLogoutMutation } from '@services/authent/login.api';
import { createErrorMessage } from '@utils/common.utils';
import { loginSchema } from '@validations/login.schema';
import { Field, FieldInputProps, Formik, FormikProps } from 'formik';
import { useCallback, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { RoutesEnum } from '@/types/routes.types';

export default function ChangeShift() {
  const validationSchema = useValidation(loginSchema);
  const { formatMessage } = useIntl();
  const { token } = useAuth();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const submitFormRef = useRef<() => void>(() => {});

  const navigate = useNavigate();
  const [login, { isLoading, isError, error }] = useLoginMutation();
  const [logoutMutation] = useLogoutMutation();
  const logout = useWithDispatch(userLogout);
  const updateIsChangingShiftWithDispatch = useWithDispatch(updateIsChangingShift);

  const handleLogin = useCallback(
    async (values: LoginRequest) => {
      login(values)
        .unwrap()
        .then((data) => {
          logoutMutation({ token, emptyStoreOnLogout: false }).finally(() => {
            logout({ token: data.token, renewalToken: data.renew });
            navigate(RoutesEnum.LOGIN);
          });
        });
    },
    [login, logout, logoutMutation, navigate, token],
  );

  useHotkeys(
    'enter',
    () => {
      submitFormRef.current();
    },
    {
      enableOnFormTags: ['input'],
    },
  );

  return (
    <Flex
      position="absolute"
      width="100%"
      height="100%"
      userSelect="none"
      backgroundColor="neutral.900-op30"
      zIndex={1000}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          updateIsChangingShiftWithDispatch(false);
        }
      }}
    >
      <Center
        position="fixed"
        width="424px"
        height="100%"
        right={0}
        gap={5}
        padding={8}
        opacity={1}
        backgroundColor="neutral.700"
      >
        <Formik
          initialValues={{
            login: '',
            password: '',
          }}
          onSubmit={handleLogin}
          validationSchema={validationSchema}
          validateOnChange
          validateOnBlur
        >
          {({ handleSubmit, errors, touched, isSubmitting, submitForm }) => {
            submitFormRef.current = submitForm;
            return (
              <Fieldset.Root invalid={!!errors.login || !!errors.password} justifyContent="center">
                <Text fontSize="20px">
                  <FormattedMessage id="authent.shiftTitle" />
                </Text>
                <Form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <Fieldset.Content>
                    <ChakraField.Root invalid={!!(touched.login && errors.login)} required>
                      <ChakraField.Label>
                        <Text textStyle="md">
                          <FormattedMessage id="authent.user.login" />
                        </Text>
                      </ChakraField.Label>
                      <Field name="login">
                        {({
                          field,
                          form,
                        }: {
                          field: FieldInputProps<string>;
                          form: FormikProps<{ name: string; surname: string }>;
                        }) => (
                          <InputGroup
                            startElement={<Icon as={IcUser} color="neutral.500" />}
                            startElementProps={{ paddingInline: 2 }}
                          >
                            <Input
                              {...field}
                              type="text"
                              style={{ paddingInlineStart: '52px' }}
                              placeholder={formatMessage({ id: 'authent.user.login' })}
                              onChange={form.handleChange}
                              onBlur={form.handleBlur}
                            />
                          </InputGroup>
                        )}
                      </Field>
                    </ChakraField.Root>
                    <ChakraField.Root invalid={!!(touched.password && errors.password)} required>
                      <ChakraField.Label>
                        <Text>
                          <FormattedMessage id="authent.user.password" />
                        </Text>
                      </ChakraField.Label>
                      <Field name="password">
                        {({
                          field,
                          form,
                        }: {
                          field: FieldInputProps<string>;
                          form: FormikProps<{ name: string; surname: string }>;
                        }) => (
                          <PasswordInput
                            {...field}
                            showLockIcon
                            onChange={form.handleChange}
                            onBlur={form.handleBlur}
                          />
                        )}
                      </Field>
                    </ChakraField.Root>
                  </Fieldset.Content>
                  {(Object.keys(errors).some((key) => touched[key as keyof typeof touched]) || isError) && (
                    <Fieldset.ErrorText>
                      <ErrorsContainer
                        touched={touched}
                        errors={errors}
                        apiError={isError ? createErrorMessage(error) : undefined}
                      />
                    </Fieldset.ErrorText>
                  )}
                  <VStack marginTop={3} gap={2} width="100%">
                    <Button type="submit" width="100%" size="md" loading={isLoading || isSubmitting}>
                      <FormattedMessage id="authent.takeShift" />
                    </Button>
                    <Button
                      variant="secondary"
                      width="100%"
                      size="md"
                      onClick={() => updateIsChangingShiftWithDispatch(false)}
                      disabled={isLoading || isSubmitting}
                    >
                      <FormattedMessage id="global.cancel" />
                    </Button>
                  </VStack>
                </Form>
                <Fieldset.HelperText>
                  <Text width="100%" textAlign="left" color="neutral.200" whiteSpace="pre-line">
                    <FormattedMessage
                      id="authent.help"
                      values={{ number: formatMessage({ id: 'global.adminNumber' }) }}
                    />
                  </Text>
                </Fieldset.HelperText>
              </Fieldset.Root>
            );
          }}
        </Formik>
      </Center>
    </Flex>
  );
}
